import React, { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Lock } from "lucide-react"
import { SENTIMENT_META, SENTIMENT_ORDER, SENTIMENT_STEPS, PURCHASE_INTENT_META, getPlatformMeta } from "../../constants"
import { formatPercent, formatNetScore, toNetScore, formatTier } from "./sentimentUtils"
import { logoutGuestAndNavigateToLogin } from "../../../../utils/loginRedirect"

export const SectionTitle = ({ children, icon: Icon, right }) => (
    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "12.5px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, margin: 0, display: "flex", alignItems: "center", gap: "7px" }}>
            {Icon && <Icon size={14} />}
            {children}
        </h6>
        {right}
    </div>
)

export const Card = ({ children, style, ...rest }) => (
    <div style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "14px", padding: "18px", ...style }} {...rest}>
        {children}
    </div>
)

export const SentimentChip = ({ sentiment, size = "md" }) => {
    const meta = SENTIMENT_META[sentiment] || SENTIMENT_META.unknown
    const small = size === "sm"
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: `${meta.color}18`, color: "#e8e8e8",
            fontSize: small ? "11px" : "12px", fontWeight: 600,
            padding: small ? "2px 8px" : "4px 10px", borderRadius: "6px", whiteSpace: "nowrap",
        }}>
            <span style={{ width: small ? 6 : 7, height: small ? 6 : 7, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
            {meta.label}
        </span>
    )
}

export const PlatformBadge = ({ id, muted = false }) => {
    const meta = getPlatformMeta(id)
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", color: muted ? "#9ca3af" : "#fff", fontWeight: 600, fontSize: "14px" }}>
            <i className={meta.icon} style={{ color: meta.color, fontSize: "14px" }} />
            {meta.label}
        </span>
    )
}

/**
 * 100% stacked distribution bar - one segment per sentiment, 2px surface gaps
 * between segments, hover tooltip with the exact share and count. Colour is
 * never the only cue: the legend beside it always carries labels + percents.
 */
export const SentimentBar = ({ percent = {}, counts = {}, height = 12 }) => {
    const [hovered, setHovered] = useState(null)
    const segments = SENTIMENT_ORDER
        .map(key => ({ key, value: Number(percent[key]) || 0, count: counts[key] }))
        .filter(s => s.value > 0)

    if (segments.length === 0) {
        return <div style={{ height, borderRadius: "4px", background: "rgba(255,255,255,0.06)" }} />
    }

    return (
        <div style={{ display: "flex", gap: "2px", height, width: "100%" }} role="img"
            aria-label={segments.map(s => `${SENTIMENT_META[s.key].label} ${formatPercent(s.value)}`).join(", ")}>
            {segments.map(seg => {
                const meta = SENTIMENT_META[seg.key]
                return (
                    <div
                        key={seg.key}
                        onMouseEnter={() => setHovered(seg.key)}
                        onMouseLeave={() => setHovered(null)}
                        style={{
                            position: "relative",
                            flex: `${seg.value} 1 0`,
                            minWidth: "4px",
                            background: meta.color,
                            borderRadius: "4px",
                            opacity: hovered && hovered !== seg.key ? 0.45 : 1,
                            transition: "opacity 0.15s ease",
                            cursor: "default",
                        }}
                    >
                        {hovered === seg.key && (
                            <div className="ugc-sent-tooltip">
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, display: "inline-block" }} />
                                <strong>{meta.label}</strong>
                                <span>{formatPercent(seg.value)}</span>
                                {seg.count != null && <span style={{ color: "#9ca3af" }}>· {seg.count} comments</span>}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export const SentimentLegend = ({ percent = {}, counts = {}, compact = false }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: compact ? "6px 14px" : "10px 22px" }}>
        {SENTIMENT_ORDER.map(key => {
            const meta = SENTIMENT_META[key]
            return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: compact ? "12px" : "13px" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "3px", background: meta.color, flexShrink: 0 }} />
                    <span style={{ color: "#9ca3af" }}>{meta.label}</span>
                    <strong style={{ color: "#fff", fontWeight: 600 }}>{formatPercent(percent[key])}</strong>
                    {!compact && counts[key] != null && <span style={{ color: "#6b7280" }}>({counts[key]})</span>}
                </div>
            )
        })}
    </div>
)

/**
 * Diverging -100..+100 track for the net score: red pole on the left, green on
 * the right, neutral centre tick, with a marker at the score.
 */
export const NetScoreTrack = ({ score }) => {
    const net = toNetScore(score)
    const position = net === null ? 50 : Math.min(100, Math.max(0, (net + 100) / 2))
    return (
        <div>
            <div style={{ position: "relative", height: "8px", borderRadius: "4px", background: "linear-gradient(90deg, rgba(239,68,68,0.55) 0%, rgba(107,114,128,0.35) 50%, rgba(16,185,129,0.55) 100%)" }}>
                <span style={{ position: "absolute", left: "50%", top: "-3px", bottom: "-3px", width: "2px", background: "rgba(255,255,255,0.35)", transform: "translateX(-1px)" }} />
                {net !== null && (
                    <span style={{
                        position: "absolute", left: `${position}%`, top: "50%",
                        width: "16px", height: "16px", borderRadius: "50%",
                        background: "#fff", border: "3px solid #1c1d27",
                        boxShadow: "0 0 0 2px rgba(255,255,255,0.25)",
                        transform: "translate(-50%, -50%)",
                    }} />
                )}
            </div>
            <div className="d-flex justify-content-between mt-2" style={{ fontSize: "11px", color: "#6b7280" }}>
                <span>-100 Negative</span>
                <span>0</span>
                <span>Positive +100</span>
            </div>
        </div>
    )
}

export const NetScore = ({ score, label = "Net sentiment", size = 44 }) => (
    <div>
        <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 500, marginBottom: "2px" }}>{label}</div>
        <div style={{ fontSize: `${size}px`, fontWeight: 700, color: "#fff", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
            {formatNetScore(score)}
        </div>
    </div>
)

export const PurchaseIntentMeter = ({ value }) => {
    const meta = PURCHASE_INTENT_META[String(value || "").toLowerCase()]
    if (!meta) return <span style={{ color: "#6b7280", fontSize: "13px" }}>Not enough signal</span>
    return (
        <div className="d-flex align-items-center gap-3">
            <div style={{ display: "flex", gap: "3px", alignItems: "flex-end" }} aria-hidden="true">
                {[1, 2, 3].map(level => (
                    <span key={level} style={{
                        width: "8px", height: `${6 + level * 5}px`, borderRadius: "2px",
                        background: level <= meta.level ? meta.color : "rgba(255,255,255,0.08)",
                    }} />
                ))}
            </div>
            <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>{meta.label}</span>
        </div>
    )
}

export const ProgressSteps = ({ stage }) => {
    const activeIndex = Math.max(0, SENTIMENT_STEPS.findIndex(step => step.stages.includes(stage)))
    const isDone = SENTIMENT_STEPS[activeIndex]?.id === "done"
    return (
        <div>
            <div style={{ display: "flex", gap: "3px" }}>
                {SENTIMENT_STEPS.map((step, i) => (
                    <span key={step.id} style={{
                        flex: 1, height: "4px", borderRadius: "2px",
                        background: i < activeIndex || isDone ? "#F6255A" : i === activeIndex ? "rgba(246,37,90,0.5)" : "rgba(255,255,255,0.08)",
                        animation: i === activeIndex && !isDone ? "ugcSentPulse 1.4s ease-in-out infinite" : "none",
                    }} />
                ))}
            </div>
            <div className="d-flex justify-content-between mt-1" style={{ fontSize: "10.5px", color: "#6b7280" }}>
                {SENTIMENT_STEPS.map((step, i) => (
                    <span key={step.id} style={{ color: i === activeIndex ? "#e8e8e8" : undefined, fontWeight: i === activeIndex ? 600 : 400 }}>{step.label}</span>
                ))}
            </div>
        </div>
    )
}

/**
 * Tier gate CTA for `app_limit_reached` / `refresh_requires_upgrade`. A guest
 * gets `redirect_to` (the login page) and is sent to sign in; tier_1 gets
 * `upgrade_to` and is sent to the plans page.
 */
export const UpgradeNotice = ({ info = {}, variant = "card" }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const isGuest = !!info.redirect_to && !info.upgrade_to
    const upgradeTier = formatTier(info.upgrade_to)
    const isRefreshBlock = info.code === "refresh_requires_upgrade"

    const title = isRefreshBlock
        ? "Sentiment has expired"
        : info.code === "app_limit_reached"
            ? "Sentiment app limit reached"
            : "Unlock comment sentiment"
    const message = info.message || info.error || (isRefreshBlock
        ? "This app's sentiment has expired with its videos' 3-month refresh. Upgrade to regenerate it."
        : "You've used all the apps your plan includes for comment sentiment.")

    const handleClick = () => {
        if (isGuest) logoutGuestAndNavigateToLogin(location.pathname + location.search, navigate)
        else navigate("/profile/plans")
    }

    const button = (
        <button onClick={handleClick} className="ugc-btn-primary" style={{ margin: variant === "card" ? "0 auto" : 0 }}>
            {isGuest ? "Sign in to continue" : upgradeTier ? `Upgrade to ${upgradeTier}` : "Upgrade Plan"}
        </button>
    )

    if (variant === "banner") {
        return (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3" style={{
                background: "rgba(246,37,90,0.06)", border: "1px solid rgba(246,37,90,0.25)",
                borderRadius: "12px", padding: "14px 18px",
            }}>
                <div className="d-flex align-items-start gap-3" style={{ minWidth: 0, flex: "1 1 320px" }}>
                    <Lock size={18} color="#F6255A" style={{ flexShrink: 0, marginTop: "2px" }} />
                    <div>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>{title}</div>
                        <div style={{ color: "#9ca3af", fontSize: "13px", lineHeight: 1.5 }}>{message}</div>
                    </div>
                </div>
                {button}
            </div>
        )
    }

    return (
        <div className="text-center" style={{ background: "rgba(246,37,90,0.05)", border: "1px solid rgba(246,37,90,0.25)", borderRadius: "16px", padding: "40px 20px" }}>
            <div style={{ width: "56px", height: "56px", background: "rgba(246,37,90,0.12)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Lock size={24} color="#F6255A" />
            </div>
            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>{title}</h6>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13.5px", maxWidth: "460px", margin: "0 auto 12px", lineHeight: "1.5" }}>
                {message}
            </p>
            {info.used != null && info.limit != null && (
                <p style={{ color: "#9ca3af", fontSize: "12.5px", margin: "0 auto 16px" }}>
                    {info.used} of {info.limit} {Number(info.limit) === 1 ? "app" : "apps"} used
                </p>
            )}
            {button}
        </div>
    )
}
